import { SelectChangeEvent } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'src/common/helper';
import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import { useNotify } from 'src/context/ContextNotify';
import { postAPI } from 'src/utils/api';
import { getToken } from 'src/utils/auth';

const inputStyle = {
  fontSize: '14px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#BDBDBD',
  },
  '& .MuiInputBase-input': { padding: '8px 14px' },
  '& input::placeholder': {
    fontSize: '14px',
    fontWeight: 500,
    color: '#CCD1D9',
    opacity: 1,
  },
  backgroundColor: 'primary.light',
  width: '100%',
}

const rowStyle = {
  '&:last-child td, &:last-child th': { border: 0 },
  '&.MuiTableRow-root .MuiTableCell-root.MuiTableCell-body': {
    py: '16px',
    borderBottom: '1px solid #F3F3F3',
  },
}

const defaultError = {
  fullname: true,
  username: true,
  password: true,
};

const Edit = (props: any) => {
  const navigate = useNavigate();
  const user = props.data?.user || null;
  const notifyContext = useNotify();
  const [errors, setErrors] = useState(defaultError);
  const [data, setData] = useState({
    alias: '',
    displayorder: '',
    fullname: '',
    isadmin: false,
    password: '',
    roleid: 2,
    status: true,
    userid: 0,
    username: '',
  });

  useEffect(() => {
    setData({
      alias: user ? (user.alias ? user.alias : '') : '',
      displayorder: user ? (user.displayorder ? user.displayorder : '') : '',
      fullname: user ? user.fullname : '',
      isadmin: user ? (user.isadmin === 1 ? true : false) : false,
      password: user ? user.password : '',
      roleid: user ? (user.roleid ? user.roleid : 2) : 2,
      status: user ? (user.status === 1 ? true : false) : true,
      userid: user ? (user.userid ? user.userid : 0) : 0,
      username: user ? user.username : '',
    });
  }, [user]);

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setData({
      ...data,
      [name]: value,
    });
  };

  const handleStatusChange = (event: SelectChangeEvent<number>) => {
    const { name, value } = event.target;
    setData({
      ...data,
      [name]: value,
    });
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = event.target;
    setData((prevState: any) => ({
      ...prevState,
      [name]: event.target.checked,
    }));
  };

  const handleCancel = () => {
    navigate(`/user/index`);
  };

  const validateForm = () => {
    if (isEmpty(data.fullname)) {
      setErrors({ ...defaultError, fullname: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '患者名が無効です',
        type: 'error',
      });
      return false;
    }
    if (isEmpty(data.username)) {
      setErrors({ ...defaultError, username: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: 'ユーザー名が無効です',
        type: 'error',
      });
      return false;
    }
    if (isEmpty(data.password)) {
      setErrors({ ...defaultError, password: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: 'パスワードを入力して下さい',
        type: 'error',
      });
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }
    try {
      const token = getToken();
      postAPI(`/auth/save?token=${token}`, { ...data }).then((res) => {
        if (res && res.code === 200) {
          notifyContext.setNotifyMessage({
            show: true,
            message: '保存出来ました。',
            type: 'success',
          });
          navigate('/user/index');
        }
        if (res.code === 501 || res.code === 500) {
          notifyContext.setNotifyMessage({
            show: true,
            message: res.msg || 'Failed to post data',
            type: 'error',
          });
        }
      });
    } catch (error) {
      console.error('Failed to post data:', error);
      return;
    }
  };
  return (
    <Stack>
      <TableContainer
        component={Paper}
        sx={{
          boxShadow: 'none',
          width: '100%',
          height: '100%',
        }}
      >
        <Table
          sx={{
            minWidth: 470,
            '& .MuiTableCell-root.MuiTableCell-head': {
              borderBottom: '1px solid #F3F3F3',
              fontWeight: 700,
              p: '6px 16px',
              fontSize: '14px',
            },
            '& .MuiTableCell-root.MuiTableCell-body': {
              color: 'primary.dark',
              fontSize: '14px',
              py: '6px',
            },
          }}
          aria-label='simple table'
        >
          <TableBody sx={{ '&.MuiTableBody-root th': { fontWeight: 700 } }}>
            <TableRow
              sx={rowStyle}
            >
              <TableCell component={'th'}>氏名</TableCell>
              <TableCell>
                <OutlinedInput
                  error={!errors.fullname}
                  placeholder={'入力してください'}
                  sx={inputStyle}
                  inputProps={{
                    sx: {
                      backgroundColor: 'primary.light',
                    },
                  }}
                  name='fullname'
                  value={data.fullname}
                  onChange={(event) => handleInputChange(event)}
                />
              </TableCell>
            </TableRow>
            <TableRow
              sx={rowStyle}
            >
              <TableCell component={'th'}>ログイン名</TableCell>
              <TableCell>
                <OutlinedInput
                  error={!errors.username}
                  placeholder={'入力してください'}
                  sx={inputStyle}
                  inputProps={{
                    sx: {
                      backgroundColor: 'primary.light',
                    },
                  }}
                  name='username'
                  value={data.username}
                  onChange={(event) => handleInputChange(event)}
                />
              </TableCell>
            </TableRow>
            <TableRow
              sx={rowStyle}
            >
              <TableCell component={'th'}>パスワード</TableCell>
              <TableCell>
                <OutlinedInput
                  error={!errors.password}
                  placeholder={'入力してください'}
                  sx={inputStyle}
                  inputProps={{
                    sx: {
                      backgroundColor: 'primary.light',
                    },
                  }}
                  name='password'
                  value={data.password}
                  onChange={(event) => handleInputChange(event)}
                  type='password'
                />
              </TableCell>
            </TableRow>
            <TableRow
              sx={rowStyle}
            >
              <TableCell component={'th'}>職種</TableCell>
              <TableCell>
                {props.data?.roles && (
                  <Select
                    className='select-status'
                    value={data.roleid}
                    name='roleid'
                    onChange={(event) => handleStatusChange(event)}
                    sx={{
                      '& .MuiSelect-select.MuiSelect-outlined': {
                        py: '5px',
                      },
                      width: '100%',
                    }}
                  >
                    {props.data.roles.map((role: any, index: number) => {
                      return (
                        <MenuItem key={index} value={role.value}>
                          {role.label}
                        </MenuItem>
                      );
                    })}
                  </Select>
                )}
              </TableCell>
            </TableRow>
            <TableRow
              sx={rowStyle}
            >
              <TableCell component={'th'}></TableCell>
              <TableCell>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={data.isadmin}
                      name='isadmin'
                      onChange={(event) => handleCheckboxChange(event)}
                    />
                  }
                  label='管理者'
                  sx={{
                    '& .MuiSvgIcon-root': { fontSize: '20px' },
                    '& .MuiTypography-root': {
                      fontSize: '14px',
                      wordBreak: 'break-word',
                    },
                  }}
                />
              </TableCell>
            </TableRow>
            <TableRow
              sx={rowStyle}
            >
              <TableCell component={'th'}>備考</TableCell>
              <TableCell>
                <OutlinedInput
                  // error={!errors.task}
                  placeholder={'入力してください'}
                  sx={inputStyle}
                  inputProps={{
                    sx: {
                      backgroundColor: 'primary.light',
                    },
                  }}
                  name='alias'
                  value={data.alias}
                  onChange={(event) => handleInputChange(event)}
                />
              </TableCell>
            </TableRow>
            <TableRow
              sx={rowStyle}
            >
              <TableCell component={'th'}>表示順位</TableCell>
              <TableCell>
                <OutlinedInput
                  // error={!errors.task}
                  placeholder={'入力してください'}
                  sx={inputStyle}
                  inputProps={{
                    sx: {
                      backgroundColor: 'primary.light',
                    },
                  }}
                  name='displayorder'
                  value={data.displayorder}
                  type='number'
                  onChange={(event) => handleInputChange(event)}
                />
              </TableCell>
            </TableRow>
            <TableRow
              sx={rowStyle}
            >
              <TableCell component={'th'}>ステータス</TableCell>
              <TableCell>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={data.status}
                      name='status'
                      onChange={(event) => handleCheckboxChange(event)}
                    />
                  }
                  label='有効'
                  sx={{
                    '& .MuiSvgIcon-root': { fontSize: '20px' },
                    '& .MuiTypography-root': {
                      fontSize: '14px',
                      wordBreak: 'break-word',
                    },
                  }}
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction={'row'} spacing={'20px'} mt={'32px'}>
        <BtnOutlineGradient
          text='キャンセル'
          handleButtonClick={handleCancel}
        />
        <BtnGradient text='登録' handleButtonClick={handleSave} />
      </Stack>
    </Stack>
  );
};

export default Edit;
